import { useState } from "react"
import { Box, Container } from "@mui/material"
import { makeStyles } from "makeStyles"
import Image from "next/image"

import { SeriesModel } from "utils/commerceTools/formatters/formatSeries"
import useProductHitImpressionTracking from "@secureo/common/hooks/tracking/useProductHitImpressionTracking"

import { BsArrowLeft, BsArrowRight } from "react-icons/bs"

import ItemsCarousel from "react-items-carousel"

import useWindowSize from "@secureo/common/hooks/useWindowSize"

const useStyles = makeStyles()(() => ({
	arrowButtonLeft: {
		position: "absolute",
		bottom: -50,
		left: "100%",
		padding: 5,
		outline: "none",
		background: "none",
		border: "none",
		cursor: "pointer",
	},
	arrowButtonRight: {
		position: "absolute",
		bottom: -50,
		right: "100%",
		padding: 5,
		outline: "none",
		background: "none",
		border: "none",
		cursor: "pointer",
	},
	card: {
		position: "relative",
		width: 300,
		height: "auto",
		"@media only screen and (max-width: 960px)": {
			width: "100%",
			minWidth: "auto",
		},
	},
	backgroundSVG: {
		width: "100%",
		height: 120,
		position: "absolute",
		bottom: 0,
		zIndex: 0,
	},
}))

const Model = ({ model, position, setModelSku, isCurrentModel, shortModelName }: ModelProps) => {
	const { name, images, sku } = model
	const image = images?.[0] ?? null

	const trackImpression = useProductHitImpressionTracking(
		{ name, grossPriceCents: 0, manufacturer: "Secureo" },
		"Modelübersicht",
		"Modelübersicht",
		position,
	)

	return (
		<Box
			mr={2}
			style={{
				cursor: "pointer",
				textAlign: "center",
			}}
			onClick={() => setModelSku(sku)}
		>
			{image && (
				<div>
					<Image
						alt={name}
						src={image}
						width={150}
						height={150}
						style={{
							maxWidth: "100%",
							height: "auto",
							objectFit: "contain",
						}}
					/>
				</div>
			)}
			<Box
				mt={1}
				style={{
					textAlign: "center",
				}}
			>
				{shortModelName} {isCurrentModel && <span>&#10004;</span>}
			</Box>
		</Box>
	)
}

interface ModelProps {
	model: SeriesModel
	shortModelName: string
	position: number
	setModelSku: (sku: string) => void
	isCurrentModel: boolean
}

const ModelOverview = ({ modelSku, setModelSku, models, name }: Props) => {
	const { classes } = useStyles()

	const [activeItemIndex, setActiveItemIndex] = useState(0)
	const chevronWidth = 0
	const { innerWidth } = useWindowSize()

	const isDesktop = innerWidth < 960
	const isTablet = innerWidth < 600

	const slideCount = !isDesktop ? 4 : isTablet ? 2 : 3

	return (
		<Box my={10} style={{ position: "relative", zIndex: 1 }}>
			<Container>
				<div>
					<h3 style={{ fontSize: 25 }}>Alle {name} in der Übersicht</h3>
				</div>
				<div style={{ position: "relative" }}>
					<ItemsCarousel
						requestToChangeActive={setActiveItemIndex}
						activeItemIndex={activeItemIndex}
						numberOfCards={slideCount}
						gutter={10}
						leftChevron={
							<button className={classes.arrowButtonLeft}>
								<BsArrowLeft size="1.8rem" color="#fff" />
							</button>
						}
						rightChevron={
							<button className={classes.arrowButtonRight}>
								<BsArrowRight size="1.8rem" color="#fff" />
							</button>
						}
						chevronWidth={chevronWidth}
						infiniteLoop={true}
						slidesToScroll={1}
						showSlither={false}
					>
						{models.map((model, i) => {
							if (!model) return null
							const shortModelName = model.name.split(name)?.[1] ?? model?.name

							return (
								<div className={classes.card} key={model.id}>
									<Model
										model={model}
										shortModelName={
											shortModelName
												? (name + shortModelName).replace("Secureo", "")
												: model?.name
										}
										position={i}
										setModelSku={setModelSku}
										isCurrentModel={modelSku === model.sku}
									/>
								</div>
							)
						})}
					</ItemsCarousel>
				</div>
			</Container>
		</Box>
	)
}

interface Props {
	modelSku: string
	setModelSku: (sku: string) => void
	models: SeriesModel[]
	name: string
}

export default ModelOverview
